import { EnumGuidePageSource } from '@calm/evexia-analytics';
import { CircleConsentTypes } from '@calm/evexia-web-sdk';
import { logger } from '@rhg/libs/logger';
import { navigateToExternalUrl } from '@rhg/utils/browser';
import type { NextRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import {
    errorBodyQueryString,
    errorTitleQueryString,
    ftueQueryString,
    guideIdQueryString,
    heroVariantQueryString,
    journalSourceQueryString,
    JournalSource,
    PackHeroVariant,
    packClassQueryString,
    packDisplayTypeQueryString,
    packIdQueryString,
    playlistIdQueryString,
    programIdQueryString,
    rowIndexQueryString,
    rowItemIndexQueryString,
    showOverviewQueryString,
    redirectQueryString,
    friendsAndFamilyIdString,
    isNewPlanQueryString,
    conciergeSourceQueryString,
    ConciergeSource,
    guidePageSourceQueryString,
    entryIdQueryString,
    emotionsCheckInEntryIdQueryString,
    emotionsCheckInSourceQueryString,
    EmotionsCheckInSource,
    notificationsTypeString,
    resultsQueryString,
    updateQueryString,
} from './constants';
import { SupportedLanguageType } from './intl';

export function addLangParam(lang: string, url?: string) {
    if (!url) {
        return '#';
    }

    const isFaqLink = url.includes('/faq');
    const typedLang = lang as SupportedLanguageType;
    const nonEnglishOrSpanish =
        typedLang !== SupportedLanguageType.EN &&
        typedLang !== SupportedLanguageType.ES;

    if (
        typedLang === SupportedLanguageType.EN ||
        (isFaqLink && nonEnglishOrSpanish)
    ) {
        return url;
    }

    return `${url}?lang=${lang}`;
}

function withLangParam(url: string) {
    return (lang: string) => addLangParam(lang, url);
}

export function omitQueryKeys(
    query: ParsedUrlQuery,
    keys: string[]
): ParsedUrlQuery {
    return Object.fromEntries(
        Object.entries(query).filter(([key]) => !keys.includes(key))
    );
}

export const getSingleStringFromQuery = (
    query: ParsedUrlQuery,
    key: string
): string | undefined => {
    const value = query[key];
    if (typeof value === 'string') {
        return value;
    }
    if (Array.isArray(value) && value.length > 0) {
        return value[0];
    }
    return undefined;
};

/**
 * Client-side `replace` that ignores superseded navigations and falls back to
 * a full page load when the router rejects for any other reason.
 */
export function replaceOrHardNavigate(
    replace: NextRouter['replace'],
    url: string,
    failureMessage: string
): Promise<void> {
    return replace(url).then(
        () => undefined,
        (error: unknown) => {
            // Next rejects with `cancelled` when a newer navigation supersedes this one.
            if ((error as { cancelled?: boolean } | null)?.cancelled) return;
            logger.error(failureMessage, {}, error);
            navigateToExternalUrl(url);
        }
    );
}

export const routes = {
    auth: '/auth',
    sso: '/auth/sso',
    ssoInfo: '/auth/sso/info',
    saml: '/saml',
    /** TODO rename to landing */
    dashboard: '/',
    reconsent: '/reconsent',
    discover: '/discover',
    discoverClinical: '/discover/clinical',
    discoverMindshift: '/discover/mindshift',
    discoverPack: (
        packClass = '',
        showPopularWithSubtitle?: boolean,
        heroVariant?: PackHeroVariant,
        rowIndex?: number,
        packDisplayType?: string
    ) => {
        const search = new URLSearchParams();
        if (showPopularWithSubtitle)
            search.set('showPopularWithSubtitle', 'true');
        if (heroVariant) search.set(heroVariantQueryString, heroVariant);
        if (rowIndex !== undefined)
            search.set(rowIndexQueryString, String(rowIndex));
        if (packDisplayType)
            search.set(packDisplayTypeQueryString, packDisplayType);
        const query = search.toString();
        return `/discover/pack/${packClass}${query ? `?${query}` : ''}`;
    },
    discoverFeed: (
        feedId = '',
        {
            heroVariant,
            rowIndex,
            rowItemIndex,
            packDisplayType,
            packId,
        }: {
            heroVariant?: PackHeroVariant;
            rowIndex?: number;
            rowItemIndex?: number;
            packDisplayType?: string;
            packId?: string;
        } = {}
    ) => {
        if (!feedId) return '/discover/feed';
        const search = new URLSearchParams();
        if (heroVariant) search.set(heroVariantQueryString, heroVariant);
        if (rowIndex !== undefined)
            search.set(rowIndexQueryString, String(rowIndex));
        if (packDisplayType)
            search.set(packDisplayTypeQueryString, packDisplayType);
        if (packId) search.set(packIdQueryString, packId);
        if (rowItemIndex !== undefined)
            search.set(rowItemIndexQueryString, String(rowItemIndex));
        const query = search.toString();
        return `/discover/feed/${feedId}${query ? `?${query}` : ''}`;
    },
    discoverDailies: (dailyId = '') =>
        dailyId ? `/discover/dailies/${dailyId}` : '/discover/dailies',
    search: '/discover?query=',
    activities: '/activities',
    favorites: '/favorites',
    guide: ({ guideId }: { guideId: string }) =>
        `${routes.guideBase}?guide_id=${guideId}`,
    guideBase: '/guide',
    health: '/health',
    login: '/login',
    careplan: '/plan',
    completed: '/completed',
    carePlanAllRecommendations: '/plan/all-recommendations',
    carePlanAdd: '/plan/add',
    emotionsCheckIn: ({ source }: { source?: EmotionsCheckInSource } = {}) =>
        `/emotions-check-in${
            source
                ? `?${new URLSearchParams({
                      [emotionsCheckInSourceQueryString]: source,
                  })}`
                : ''
        }`,
    emotionsCheckInResults: ({
        entryId,
        source,
    }: {
        entryId: number;
        source?: EmotionsCheckInSource;
    }) =>
        `${routes.emotionsCheckInResultsBase}?${new URLSearchParams({
            [emotionsCheckInEntryIdQueryString]: String(entryId),
            ...(source ? { [emotionsCheckInSourceQueryString]: source } : {}),
        })}`,
    emotionsCheckInResultsBase: '/emotions-check-in/results',
    emotionsCheckInHistory: '/emotions-check-in/history',
    emotionsCheckInHistoryAll: '/emotions-check-in/history/all',
    emotionsCheckInEmotionHistory: (emotionId: string) =>
        `/emotions-check-in/history/${emotionId}`,
    expired: '/expired',
    profile: '/profile',
    profileInvite: (params: { queryId?: string }) =>
        `/profile/invite${
            params.queryId
                ? `?${new URLSearchParams({
                      [friendsAndFamilyIdString]: params.queryId,
                  })}`
                : ''
        }`,
    profileInviteStatus: (params: { queryId?: string }) =>
        `/profile/invite/status${
            params.queryId
                ? `?${new URLSearchParams({
                      [friendsAndFamilyIdString]: params.queryId,
                  })}`
                : ''
        }`,
    programDetails: (
        packClass = '',
        programId = '',
        showOverview = '',
        additionalParams: Record<string, string> = {}
    ) =>
        `${routes.programBase}${
            programId
                ? `?${new URLSearchParams({
                      ...(packClass
                          ? { [packClassQueryString]: packClass }
                          : {}),
                      ...(programId
                          ? { [programIdQueryString]: programId }
                          : {}),
                      ...(showOverview
                          ? { [showOverviewQueryString]: showOverview }
                          : {}),
                      ...additionalParams,
                  }).toString()}`
                : ''
        }`,
    programBase: '/program',
    playlist: '/playlist',
    playlists: '/playlists',
    playlistDetails: ({ playlistId }: { playlistId: number | string }) =>
        `${routes.playlist}?${new URLSearchParams({
            [playlistIdQueryString]: String(playlistId),
        }).toString()}`,
    recent: '/recent',
    resources: '/resources',
    screening: ({
        packClass = '',
        programId = '',
        isFtue = false,
        skipToQuestion = false,
        isRetrigger = false,
        redirect,
    }: {
        packClass?: string;
        programId?: string;
        isFtue?: boolean;
        redirect?: string;
        skipToQuestion?: boolean;
        isRetrigger?: boolean;
    }) =>
        `/screening${
            !!packClass ||
            !!programId ||
            isFtue ||
            redirect ||
            skipToQuestion ||
            isRetrigger
                ? `?${new URLSearchParams({
                      ...(packClass
                          ? { [packClassQueryString]: packClass }
                          : {}),
                      ...(programId
                          ? { [programIdQueryString]: programId }
                          : {}),
                      ...(redirect ? { [redirectQueryString]: redirect } : {}),
                      ...(isFtue ? { [ftueQueryString]: 'true' } : {}),
                      ...(skipToQuestion ? { skipToQuestion: 'true' } : {}),
                      ...(isRetrigger ? { isRetrigger: 'true' } : {}),
                  })}`
                : ''
        }`,
    signup: '/signup',
    topics: (params: { isFtue: boolean }) =>
        `/topics${
            params.isFtue
                ? `?${new URLSearchParams({ [ftueQueryString]: 'true' })}`
                : ''
        }`,
    goals: (params: { isFtue: boolean }) =>
        `/goals${
            params.isFtue
                ? `?${new URLSearchParams({ [ftueQueryString]: 'true' })}`
                : ''
        }`,
    conditions: (params: { isFtue: boolean }) =>
        `/conditions${
            params.isFtue
                ? `?${new URLSearchParams({ [ftueQueryString]: 'true' })}`
                : ''
        }`,
    results: (params: { isFtue: boolean }) =>
        `/results${
            params.isFtue
                ? `?${new URLSearchParams({ [ftueQueryString]: 'true' })}`
                : ''
        }`,
    registration: '/registration',
    emailOptInConfirm: '/registration/email-communications-confirm',
    onboarding: (params: { results?: boolean }) =>
        `/onboarding${
            params.results
                ? `?${new URLSearchParams({ [resultsQueryString]: 'true' })}`
                : ''
        }`,
    concierge: (params: {
        results?: boolean;
        isUpdating?: boolean;
        isNewPlan?: boolean;
        source?: ConciergeSource;
        guidePageSource?: EnumGuidePageSource;
    }) =>
        `/concierge${
            params.results ||
            params.isUpdating ||
            params.isNewPlan ||
            params.source ||
            params.guidePageSource
                ? `?${new URLSearchParams({
                      ...(params.results
                          ? { [resultsQueryString]: 'true' }
                          : {}),
                      ...(params.isUpdating
                          ? { [updateQueryString]: 'true' }
                          : {}),
                      ...(params.isNewPlan
                          ? { [isNewPlanQueryString]: 'true' }
                          : {}),
                      ...(params.source
                          ? { [conciergeSourceQueryString]: params.source }
                          : {}),
                      ...(params.guidePageSource
                          ? {
                                [guidePageSourceQueryString]:
                                    params.guidePageSource,
                            }
                          : {}),
                  })}`
                : ''
        }`,
    notFound: '/404',
    logout: '/logout',
    partnerMemberActivation: '/partner-member-activation',
    error: (params: { title?: string; body?: string }) =>
        `/500${
            params.title || params.body
                ? `?${new URLSearchParams({
                      ...(params.title
                          ? { [errorTitleQueryString]: params.title }
                          : {}),
                      ...(params.body
                          ? { [errorBodyQueryString]: params.body }
                          : {}),
                  }).toString()}`
                : ''
        }`,
    settings: '/profile/settings',
    notifications: (value = '') =>
        `/profile/notifications${
            value
                ? `?${new URLSearchParams({
                      ...(value ? { [notificationsTypeString]: value } : {}),
                  }).toString()}`
                : ''
        }`,
    sponsor: '/profile/sponsor',
    standaloneScreening: '/mental-health-screening',
    standaloneResults: '/mental-health-screening-results',
    nonUsResident: '/non-us-resident',
    journaling: '/journal',
    journalEntry: (
        params: {
            programId?: string;
            guideId?: string;
            source?: JournalSource;
        } = {}
    ) => {
        const search = new URLSearchParams();
        if (params.programId)
            search.set(programIdQueryString, params.programId);
        if (params.guideId) search.set(guideIdQueryString, params.guideId);
        if (params.source) search.set(journalSourceQueryString, params.source);
        const query = search.toString();
        return `/journal/entry${query ? `?${query}` : ''}`;
    },
    journalEntryReview: (params: { queryId?: string }) =>
        `/journal/review${
            params.queryId
                ? `?${new URLSearchParams({
                      [entryIdQueryString]: params.queryId,
                  })}`
                : ''
        }`,
    external: {
        helpline: withLangParam('https://calmhealth.findahelpline.com/'),
        consentFallback: {
            chdpp: withLangParam(
                'https://health.calm.com/consent-to-collect-consumer-health-data/'
            ),
        } as Partial<
            Record<CircleConsentTypes, ReturnType<typeof withLangParam>>
        > & { chdpp: ReturnType<typeof withLangParam> },
        tos: withLangParam('https://health.calm.com/calm-health-terms/'),
        faq: withLangParam('https://www.calmhealth.com/faq'), // No health.calm.com equivalent yet
        manageNotifications: withLangParam(
            'https://support.calm.com/hc/en-us/articles/31817784274715-Managing-Your-Calm-Health-Notifications'
        ),
        cookieNotice: withLangParam(
            'https://health.calm.com/calm-health-cookies/'
        ),
        privacyPolicy: withLangParam(
            'https://health.calm.com/calm-health-privacy-policy/'
        ),
        consumerHealthDataPrivacyPolicy: withLangParam(
            'https://health.calm.com/consumer-health-data-privacy-policy/'
        ),
        calmScreeningLandingPage: withLangParam('https://screening.calm.com'),
        /**
         * This app store deeplink is in a new AppsFlyer template set, and thus
         * doesn't share the id used elsewhere. This means we can't just update the
         * NEXT_PUBLIC_LINK_DEEPLINK_BASE_URL env var that includes the template id
         * for any deeplinks around the app that leverage /careplan, /profile, etc.
         * since those deeplinks are not set up within this template group.
         *
         * AppsFlyer link structure: https://link.calmhealth.com/[template Id]/[link Id]
         */
        appDownloadDeeplink: 'https://link.calmhealth.com/S4oG/tf7n67c6',
        // Requires a `deep_link_sub1={accessCode}` query param to attribute the download to a partner.
        partnerActivateDeeplink: 'https://link.calmhealth.com/cfkt/activate',
        employersAndPayers: withLangParam(
            'https://health.calm.com/health-plans/'
        ),
        providers: withLangParam('https://health.calm.com/health-plans/'),
        press: withLangParam('https://health.calm.com/resources/press/'),
        calm: withLangParam('https://calm.com'),
        calmBusiness: withLangParam('https://business.calm.com/'),
        calmHealth: withLangParam('https://health.calm.com/'),
        requestInformation: withLangParam(
            'https://health.calm.com/contact-us/'
        ),
        contactUs:
            'https://support.calm.com/hc/en-us/requests/new?ticket_form_id=40130435443483',
        careers: withLangParam('https://www.calm.com/careers'),
        resources: withLangParam('https://www.calmhealth.com/resources'), // No health.calm.com equivalent yet
        optOut: withLangParam('https://www.calmhealth.com/optout'),

        findAHelpline: 'https://www.findahelpline.com/',
        linkedIn: 'https://www.linkedin.com/company/calm-com/',
        youtube: 'https://www.youtube.com/@calm',

        forHealthPlans: withLangParam('https://health.calm.com/health-plans/'),
        forEmployers: withLangParam('https://health.calm.com/employers/'),
        forConsultants: withLangParam('https://health.calm.com/consultants/'),
        forOrganizationsUnder300: withLangParam(
            'https://health.calm.com/calm-for-organizations/'
        ),
        ourExperts: withLangParam('https://health.calm.com/our-experts/'),
        adminLogin: 'https://partner.calm.com/',
        healthResources: withLangParam('https://health.calm.com/resources/'),
        helpCenter: withLangParam(
            'https://support.calm.com/hc/en-us/categories/26888021635611-Calm-Health'
        ),
        ccpaNotice:
            'https://www.calm.com/privacy-policy#information-for-california-residents',
    },
};

/**
 * Parses an in-app path such as a router `asPath`. A relative path is read
 * against a placeholder origin; an unparseable one falls back to splitting.
 */
export function parseAppPath(
    path: string
): Pick<URL, 'pathname' | 'searchParams'> {
    try {
        return new URL(
            path.startsWith('/') ? `https://example.com${path}` : path
        );
    } catch {
        const [pathname, query = ''] = path.split('#')[0].split('?');
        return { pathname, searchParams: new URLSearchParams(query) };
    }
}

/** Pages back navigation never returns to. */
export const IGNORED_BACK_ROUTES = [routes.auth, routes.sso, routes.saml];

/**
 * Index of the latest history entry back navigation may return to: not a
 * `#` URL, not an ignored page and not one `isSkipped` rejects; -1 if none.
 */
export function getBackRouteIndex(
    history: string[],
    isSkipped: (entry: string) => boolean
) {
    for (let index = history.length - 1; index >= 0; index--) {
        const entry = history[index];
        if (
            !entry.includes('#') &&
            !IGNORED_BACK_ROUTES.includes(parseAppPath(entry).pathname) &&
            !isSkipped(entry)
        ) {
            return index;
        }
    }
    return -1;
}

export const routePattern = {
    partnerMemberActivationToken: `${routes.partnerMemberActivation}/[token]`,
    discoverGoal: `${routes.discover}/goal/[goal]`,
    discoverTopic: `${routes.discover}/topic/[topic]`,
};
